import { Device } from "@/__generated__/graphql";
import { FormValues } from "@features/createDeviceConfiguration/type/FormValues";
import { Typography, Grid } from "@mui/material";
import { Control, useWatch, UseFormSetValue } from "react-hook-form";
import { useEffect, useMemo } from "react";
import OptionSelector from "../../OptionSelector";
import { FormInputText } from "@components/form";
import useHardwareOptions from "@features/createDeviceConfiguration/hooks/useHardwareOptions";
import { ENABLED_SENSOR_TYPES } from "@features/createDeviceConfiguration/data/initialData";

const DOPPLER_MODEL_LABEL = "Doppler LanryBox DOF6000";

interface DeviceSensorsFormProps {
  device: Device;
  control: Control<FormValues>;
  setValue: UseFormSetValue<FormValues>;
}

const LABEL_TO_NAME_MAP: Record<string, string> = {
  'Nivel': 'level',
  'Velocidad': 'velocity',
  'Flujo': 'flow',
  'Inclinómetro': 'angle',
  'Potenciómetro': 'potentiometer',
};

const DeviceSensorsForm = ({
  device,
  control,
  setValue,
}: DeviceSensorsFormProps) => {
  const childDevice = device?.relatedDevices?.find(rel => rel.relation === 'CHILD')?.device;
  const isParentDevice = !!childDevice;
  // Watch all sensor model options to conditionally render serial number fields
  const watchedSensors = useWatch({
    control,
    name: 'sensors',
    defaultValue: [],
  });

  // Identify level and velocity sensor indices
  const levelSensorIndex = useMemo(() =>
    (device.sensors ?? [])
      .filter(sensor => ENABLED_SENSOR_TYPES.includes(sensor.type.label ?? ""))
      .findIndex(sensor => sensor.type.label === 'Nivel'),
    [device.sensors]
  );

  const velocitySensorIndex = useMemo(() =>
    (device.sensors ?? [])
      .filter(sensor => ENABLED_SENSOR_TYPES.includes(sensor.type.label ?? ""))
      .findIndex(sensor => sensor.type.label === 'Velocidad'),
    [device.sensors]
  );

  // Watch level sensor fields for real-time sync
  const levelSensorData = useWatch({
    control,
    name: levelSensorIndex !== -1 ? `sensors.${levelSensorIndex}` : undefined,
    defaultValue: undefined,
  });

  // Check if Doppler model is selected for level sensor
  const isDopplerSelected = useMemo(() => {
    if (levelSensorIndex === -1) return false;
    const levelSensorModel = watchedSensors?.[levelSensorIndex]?.sensorModelOption;
    return levelSensorModel?.name === DOPPLER_MODEL_LABEL;
  }, [watchedSensors, levelSensorIndex]);

  // Get velocity sensor options for proper object reference
  const velocitySensorOptions = useHardwareOptions('velocity_sensor').options;

  // Sync level sensor data to velocity sensor when Doppler is selected
  useEffect(() => {
    if (!isDopplerSelected || levelSensorIndex === -1 || velocitySensorIndex === -1) return;

    const levelSensor = levelSensorData || watchedSensors?.[levelSensorIndex];
    if (!levelSensor) return;

    // Fields to sync from level to velocity (excluding sensorModelOption which needs special handling)
    const fieldsToSync: Array<keyof typeof levelSensor> = [
      'serialNumber',
      'modbusId',
      'batchNumber',
      'integrationDetail',
      'attributes'
    ];

    // Sync regular fields
    fieldsToSync.forEach(field => {
      const levelValue = levelSensor[field];
      setValue(`sensors.${velocitySensorIndex}.${field}` as any, levelValue, {
        shouldDirty: true,
        shouldValidate: false,
      });
    });

    // Special handling for sensorModelOption: find the correct object from velocity options by name
    if (levelSensor.sensorModelOption) {
      const matchingOption = velocitySensorOptions.find(
        opt => opt.name === levelSensor.sensorModelOption.name
      );
      if (matchingOption) {
        setValue(`sensors.${velocitySensorIndex}.sensorModelOption` as any, matchingOption, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    }
  }, [isDopplerSelected, levelSensorIndex, velocitySensorIndex, levelSensorData, watchedSensors, velocitySensorOptions, setValue]);

  // Clear velocity data when Doppler is deselected (excluding factorM and offsetN)
  useEffect(() => {
    if (isDopplerSelected || velocitySensorIndex === -1) return;

    const velocitySensor = watchedSensors?.[velocitySensorIndex];
    const velocityHadDoppler = velocitySensor?.sensorModelOption?.name === DOPPLER_MODEL_LABEL;

    if (velocityHadDoppler) {
      const fieldsToSync = [
        'sensorModelOption',
        'serialNumber',
        'modbusId',
        'batchNumber',
        'integrationDetail',
        'attributes'
      ] as const;

      fieldsToSync.forEach(field => {
        setValue(`sensors.${velocitySensorIndex}.${field}` as any, null, {
          shouldDirty: true,
          shouldValidate: false
        });
      });
    }
  }, [isDopplerSelected, velocitySensorIndex, watchedSensors, setValue]);

  // Clear integrationDetail when sensorModelOption changes and is not "integration"
  useEffect(() => {
    watchedSensors?.forEach((sensor, index) => {
      const isIntegration = sensor?.sensorModelOption?.name === 'Integración';
      if (!isIntegration && sensor?.integrationDetail) {
        setValue(`sensors.${index}.integrationDetail` as any, null, { shouldDirty: true });
      }
    });
  }, [watchedSensors, setValue]);
  const attributesFMX = [
    { key: "measurementRange", label: "Rango de medición" },
    { key: "cableLength", label: "Largo de cable (m)" },
    { key: "depth", label: "Profundidad (cm)" },
    { key: "fmx21Code", label: "Código FMX21" }
  ];

  const attributesFlowmeter = [
    { key: "pulseValue", label: "Valor de pulso" },
    { key: "pulseWidth", label: "Ancho del pulso" }
  ];
  return (
    <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
      {(device.sensors ?? []).filter(sensor => ENABLED_SENSOR_TYPES.includes(sensor.type.label ?? "")).map((sensor, index) => {
        const sensorTypeName = LABEL_TO_NAME_MAP[sensor.type.label ?? ""] ?? sensor.type.name;

        // Determine if integration detail should be shown for this sensor
        const currentSensorModel = watchedSensors?.[index]?.sensorModelOption;
        const isIntegration = currentSensorModel?.name === 'Integración';
        const isFMX = currentSensorModel?.name === 'FMX21';

        // Check if this is the velocity sensor and Doppler is selected for level
        const isVelocitySensor = sensor.type.label === 'Velocidad';
        const shouldDisableVelocity = isVelocitySensor && isDopplerSelected;

        // Create a unique key for velocity sensor that changes when synchronized values change
        // Exclude factorM and offsetN from the key since they're editable
        const sensorKey = isVelocitySensor && isDopplerSelected
          ? `${index}-${JSON.stringify({
              sensorModelOption: watchedSensors?.[index]?.sensorModelOption,
              serialNumber: watchedSensors?.[index]?.serialNumber,
              modbusId: watchedSensors?.[index]?.modbusId,
              batchNumber: watchedSensors?.[index]?.batchNumber,
              integrationDetail: watchedSensors?.[index]?.integrationDetail,
              attributes: watchedSensors?.[index]?.attributes
            })}`
          : index;

        return (
        <Grid item xs={12} md={6} key={sensorKey}>
          <Typography variant="h6" color="textSecondary" mt={2} ml={1}>
            Sensor {index + 1} de {sensor?.type?.label}{sensor.childDeviceId && sensor.childDeviceId !== device.id  ? ` - ID ${sensor.childDeviceId}` : ''}
            {shouldDisableVelocity && (
              <Typography component="span" variant="caption" color="info.main" sx={{ ml: 1 }}>
                (Sincronizado con sensor de nivel)
              </Typography>
            )}
          </Typography>
          <Grid item xs={12} mt={2}>
            <OptionSelector
              isDisabled={isParentDevice || shouldDisableVelocity}
              options={
                useHardwareOptions(`${sensorTypeName}_sensor`).options
              }
              label="Tipo de sensor *"
              name={`sensors[${index}].sensorModelOption`}
              control={control}
            />
          </Grid>
          {isIntegration && (
            <Grid item xs={12} mt={2}>
              <FormInputText
                disabled={isParentDevice || shouldDisableVelocity}
                name={`sensors[${index}].integrationDetail`}
                control={control}
                label="Detalle de integración"
              />
            </Grid>
          )}
          {isFMX && attributesFMX.map(attr => (
            <Grid item xs={12} mt={2} key={attr.key}>
              <FormInputText
                disabled={isParentDevice || shouldDisableVelocity}
                name={`sensors[${index}].attributes.${attr.key}`}
                control={control}
                label={attr.label}
              />
            </Grid>
          ))}
          <Grid item xs={12} mt={2}>
            <FormInputText
              disabled={isParentDevice || shouldDisableVelocity}
              name={`sensors[${index}].serialNumber`}
              control={control}
              label="Número de serie del sensor"
            />
          </Grid>
          <Grid item xs={12} mt={2}>
            <FormInputText
              disabled={isParentDevice || shouldDisableVelocity}
              name={`sensors[${index}].modbusId`}
              control={control}
              label="Modbus ID del sensor"
            />
          </Grid>
          <Grid item xs={12} mt={2}>
            <FormInputText
              disabled={isParentDevice || shouldDisableVelocity}
              name={`sensors[${index}].batchNumber`}
              control={control}
              label="Número de lote del sensor"
            />
          </Grid>
          <Grid item xs={12} mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormInputText
                  disabled={isParentDevice}
                  name={`sensors[${index}].factorM`}
                  control={control}
                  label="Factor M"
                  type="number"
                />
              </Grid>
              <Grid item xs={6}>
                <FormInputText
                  disabled={isParentDevice}
                  name={`sensors[${index}].offsetN`}
                  control={control}
                  label="Offset N"
                  type="number"
                />
              </Grid>
            </Grid>
          </Grid>
          {sensor.type.label === "Flujo" && (
            <Grid item xs={12} mt={2}>
              <Grid container spacing={2}>
                {attributesFlowmeter.map(attr => (
                <Grid item xs={6} key={attr.key}>
                  <FormInputText
                    disabled={isParentDevice || shouldDisableVelocity}
                    name={`sensors[${index}].attributes.${attr.key}`}
                    control={control}
                    label={attr.label}
                  />
                </Grid>
              ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      )})}
    </Grid>
  );
}

export default DeviceSensorsForm;
